import React, { useRef, useState } from "react";

import XCloseIcon from "@uui/XClose";
import { find, isEqual } from "lodash/fp";

import type { SelectChangeEvent } from "@mui/material";
import { Collapse, FormControlLabel } from "@mui/material";
import Typography from "@mui/material/Typography";

import MinimumNoticeInput, {
  MINIMUM_NOTICE_BUFFER,
} from "@app/analytics/components/MinimumNoticeInput";
import Modal from "@app/analytics/components/Modal";
import PreferredPlatformSelector from "@app/analytics/components/PreferredPlatformSelector";
import type {
  PatchCustomSchedulingLinkBody,
  RssLinkPlatformOption,
  RssLinkPlatformPreference,
  SchedulingLinkBookingWindow,
  SchedulingLinkDuration,
} from "@app/common/types";
import type { CalendarProviderId } from "@app/common/types/providerTypes";

import Button from "@rui/Button";
import Checkbox from "@rui/Checkbox";
import Dropdown, { DropdownMenuItem } from "@rui/Dropdown";
import IconButton from "@rui/IconButton";
import Stack, { Row } from "@rui/Stack";
import TextField from "@rui/TextField";

import {
  BOOKING_WINDOW_OPTIONS,
  MEETING_DURATION_DROPDOWN_ITEMS,
} from "./constants";

export type SchedulingLinkFormValues = Pick<
  PatchCustomSchedulingLinkBody,
  | "allowedDuration"
  | "bookingWindow"
  | "minimumSchedulingBuffer"
  | "platformPreference"
  | "title"
>;

interface SchedulingLinkModalProps {
  initialValues?: SchedulingLinkFormValues;
  isSaving: boolean;
  onClose: () => void;
  onConnectCalendar: (calendarProviderId: CalendarProviderId) => void;
  onSave: (
    formValues: SchedulingLinkFormValues,
    { onSuccess }: { onSuccess: () => void }
  ) => void;
  open: boolean;
  platformOptions: RssLinkPlatformOption[];
  title: string;
}

const SchedulingLinkModal = ({
  initialValues,
  isSaving,
  onClose,
  onSave,
  open,
  title,
  ...platformPreferenceProps
}: SchedulingLinkModalProps) => {
  const [formValues, setFormValues] = useState<SchedulingLinkFormValues>({});
  const [minimumNoticeBufferInputFocused, setMinimumNoticeBufferInputFocused] =
    useState(false);

  const nameFieldRef = useRef<HTMLInputElement | null>(null);

  const mergedFormValues: SchedulingLinkFormValues = {
    ...(initialValues ?? {}),
    ...formValues,
  };

  const formIsIncomplete =
    !mergedFormValues.allowedDuration ||
    !mergedFormValues.bookingWindow ||
    !mergedFormValues.platformPreference ||
    !mergedFormValues.title;

  const saveDisabled =
    formIsIncomplete ||
    (initialValues != null && isEqual(mergedFormValues, initialValues)) ||
    (mergedFormValues.minimumSchedulingBuffer != null &&
      mergedFormValues.minimumSchedulingBuffer > MINIMUM_NOTICE_BUFFER.MAX);

  const handleChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormValues((currValues) => ({
      ...currValues,
      title: event.target.value,
    }));
  };

  const handleChangeDuration = (event: SelectChangeEvent) => {
    setFormValues((currValues) => ({
      ...currValues,
      allowedDuration: parseInt(event.target.value) as SchedulingLinkDuration,
    }));
  };

  const handleChangePlatform = (
    platformPreference: RssLinkPlatformPreference
  ) => {
    setFormValues((currValues) => ({
      ...currValues,
      platformPreference,
    }));
  };

  const handleChangeBookingWindow = (event: SelectChangeEvent) => {
    setFormValues((currValues) => ({
      ...currValues,
      bookingWindow: event.target.value as SchedulingLinkBookingWindow,
    }));
  };

  const handleToggleMinimumNotice = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setFormValues((currValues) => ({
      ...currValues,
      minimumSchedulingBuffer: checked
        ? initialValues?.minimumSchedulingBuffer ||
          MINIMUM_NOTICE_BUFFER.DEFAULT
        : MINIMUM_NOTICE_BUFFER.MIN,
    }));
  };

  const handleChangeMinimumNotice = (newValue: number) => {
    setFormValues((currValues) => ({
      ...currValues,
      minimumSchedulingBuffer: newValue,
    }));
  };

  const handleClose = () => {
    onClose();
    setFormValues({});
  };

  const handleSave = () => onSave(formValues, { onSuccess: handleClose });

  const selectedDurationItem = find(
    { value: `${mergedFormValues.allowedDuration}` },
    MEETING_DURATION_DROPDOWN_ITEMS
  );
  const selectedBookingWindowItem = find(
    { value: mergedFormValues.bookingWindow },
    BOOKING_WINDOW_OPTIONS
  );

  const hasMinimumNoticeBuffer =
    (mergedFormValues.minimumSchedulingBuffer ?? 0) > 0;

  const showMinimumNoticeBufferInput =
    hasMinimumNoticeBuffer || minimumNoticeBufferInputFocused;

  return (
    <Modal onClose={handleClose} open={open} sx={{ p: 3 }}>
      <Stack sx={{ width: "500px", maxWidth: "100%" }}>
        <Row variant="apart" sx={{ mt: -1, mr: -1 }}>
          <Typography variant="h3" fontWeight={500}>
            {title}
          </Typography>
          <IconButton size="small" onClick={handleClose}>
            <XCloseIcon />
          </IconButton>
        </Row>
        <Typography variant="labelReg" color="shades.neutral.500">
          Custom links allow you to send scheduling links with custom
          restraints.
        </Typography>
        <Stack spacing={2} sx={{ my: 2, overflowY: "auto" }}>
          <Stack spacing={0.5}>
            <Typography variant="bodySecondaryMed">Custom link name</Typography>
            <TextField
              name="title"
              onChange={handleChangeName}
              placeholder="New custom link"
              value={mergedFormValues.title}
              ref={nameFieldRef}
              fullWidth
              sx={{
                minWidth: 0,
                "& .MuiInputBase-root": { minHeight: "48px" },
                "& input": { fontSize: "0.875rem" },
              }}
            />
          </Stack>
          <Stack spacing={0.5}>
            <Typography variant="bodySecondaryMed">Meeting duration</Typography>
            <Dropdown
              name="allowedDuration"
              placeholder="Select one"
              onChange={handleChangeDuration}
              selectedItem={selectedDurationItem}
              sx={{ minHeight: "48px" }}
            >
              {MEETING_DURATION_DROPDOWN_ITEMS.map(({ value, label }) => (
                <DropdownMenuItem
                  key={value}
                  label={label}
                  value={value}
                  selected={value === `${mergedFormValues.allowedDuration}`}
                />
              ))}
            </Dropdown>
          </Stack>
          <Stack spacing={0.5}>
            <Typography variant="bodySecondaryMed">
              Conferencing platform
            </Typography>
            <PreferredPlatformSelector
              {...platformPreferenceProps}
              onChange={handleChangePlatform}
              selected={mergedFormValues.platformPreference}
              sx={{ minHeight: "48px" }}
            />
          </Stack>
          <Stack spacing={0.5}>
            <Typography variant="bodySecondaryMed">Allow scheduling</Typography>
            <Dropdown
              name="bookingWindow"
              placeholder="Select one"
              onChange={handleChangeBookingWindow}
              selectedItem={selectedBookingWindowItem}
              sx={{ minHeight: "48px" }}
            >
              {BOOKING_WINDOW_OPTIONS.map(({ value, label }) => (
                <DropdownMenuItem
                  key={value}
                  label={label}
                  value={value}
                  selected={value === mergedFormValues.bookingWindow}
                />
              ))}
            </Dropdown>
          </Stack>
          <Stack spacing={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showMinimumNoticeBufferInput}
                  onChange={handleToggleMinimumNotice}
                />
              }
              label={
                <Typography variant="bodySecondaryMed">
                  Require minimum notice for scheduling
                </Typography>
              }
              sx={{ ml: -1 }}
            />
            <Collapse in={showMinimumNoticeBufferInput}>
              <MinimumNoticeInput
                onBlur={() => setMinimumNoticeBufferInputFocused(false)}
                onChange={handleChangeMinimumNotice}
                onFocus={() => setMinimumNoticeBufferInputFocused(true)}
                value={mergedFormValues.minimumSchedulingBuffer ?? 0}
              />
            </Collapse>
          </Stack>
        </Stack>
        <Row variant="right" spacing={1} paddingTop={2}>
          <Button
            variant="text"
            sx={{ minWidth: "100px" }}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            loading={isSaving}
            disabled={saveDisabled}
            onClick={handleSave}
          >
            Save custom link
          </Button>
        </Row>
      </Stack>
    </Modal>
  );
};

export default SchedulingLinkModal;
